import React, { useState, useCallback } from 'react';
import { Card, Table, Button, message, DatePicker } from 'antd';
import { SearchOutlined, ExportOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { PAGE_QUERY_REALTIME_SETTLEMENT_DAILY_SUM } from '../../../graphql/queries/settlement/realtimeSettlementOrderDailySumQueries';
import { EXPORT_REALTIME_SETTLEMENT_DAILY_SUM } from '../../../graphql/mutations/settlement/realtimeSettlementOrderDailySumMutations';
import { ChannelSelectFilter } from '../../../components/settlement/ChannelSelectFilter';
import TablePaginationBar from '../../../components/TablePaginationBar';
import ParcelsDownloadModal from './components/ParcelsDownloadModal';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type {
    RealtimeEstimatedIncomeFilterInput,
} from '../../../graphql/types/settlement/realtimeSettlementOrderDailySumTypes';
import { getCurrencyName, formatPlainAmountFromCents } from '../../../utils/settlementAmountFormatter';
import { resolvePricingSchemeMessage } from '../../../utils/graphqlUtils';

const { RangePicker } = DatePicker;

const RealtimeEstimatedIncomePage: React.FC<{ sumType: number }> = ({ sumType }) => {
    const [filters, setFilters] = useState<RealtimeEstimatedIncomeFilterInput>({});
    const [tempFilters, setTempFilters] = useState<RealtimeEstimatedIncomeFilterInput>({});
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');

    const { data, loading } = useQuery(PAGE_QUERY_REALTIME_SETTLEMENT_DAILY_SUM, {
        variables: {
            filters: {
                clientIds: filters.clientId ? [filters.clientId] : undefined,
                dailyDateStart: filters.dailyDateStart,
                dailyDateEnd: filters.dailyDateEnd,
                summaryType: sumType,
            },
            page,
            pageSize,
        },
        fetchPolicy: 'cache-and-network',
    });

    const [exportDailySum] = useMutation(EXPORT_REALTIME_SETTLEMENT_DAILY_SUM);

    const handleSearch = useCallback(() => {
        setFilters(tempFilters);
        setPage(1);
    }, [tempFilters]);

    const handleReset = () => {
        setTempFilters({});
        setFilters({});
        setPage(1);
    };

    const handleExport = async () => {
        try {
            const result = await exportDailySum({
                variables: {
                    filters: {
                        clientIds: filters.clientId ? [filters.clientId] : undefined,
                        dailyDateStart: filters.dailyDateStart,
                        dailyDateEnd: filters.dailyDateEnd,
                        summaryType: sumType,
                    },
                },
            });
            const exportResult = result.data?.exportRealtimeSettlementDailySum;
            const fileName = exportResult?.data
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadModalVisible(true);
            } else {
                message.error(resolvePricingSchemeMessage(exportResult, '导出失败'));
            }
        } catch (error) {
            message.error('导出失败: ' + (error as Error).message);
        }
    };

    const pageResult = data?.pageQueryRealtimeSettlementDailySum;

    const columns = [
        {
            title: '客户名称',
            dataIndex: 'clientName',
            key: 'clientName',
            width: 180,
            fixed: 'left' as const,
        },
        {
            title: '客户ID',
            dataIndex: 'clientId',
            key: 'clientId',
            width: 120,
        },
        {
            title: '日期',
            dataIndex: 'dailyDate',
            key: 'dailyDate',
            width: 120,
            render: (date: string) => {
                if (!date) return '-';
                const cleaned = date.replace(/[-\s]/g, '');
                if (cleaned.length === 8) {
                    return `${cleaned.substring(0, 4)}.${cleaned.substring(4, 6)}.${cleaned.substring(6, 8)}`;
                }
                return date;
            },
        },
        {
            title: '币种',
            dataIndex: 'currency',
            key: 'currency',
            width: 100,
            render: (currency: string) => getCurrencyName(currency),
        },
        {
            title: '订单数',
            dataIndex: 'orderCount',
            key: 'orderCount',
            width: 100,
        },
        {
            title: '收入金额',
            dataIndex: 'sumAmount',
            key: 'sumAmount',
            width: 150,
            render: (sumAmount: string) => formatPlainAmountFromCents(sumAmount),
        },
        {
            title: '时区',
            dataIndex: 'timezone',
            key: 'timezone',
            width: 150,
        },
    ];

    return (
        <div className="p-6 space-y-4">
            <Card title="筛选条件">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <ChannelSelectFilter
                            stacked
                            label="客户名称"
                            placeholder="搜索客户..."
                            value={tempFilters.clientId != null ? String(tempFilters.clientId) : undefined}
                            onChange={(val) => {
                                setTempFilters((prev) => ({
                                    ...prev,
                                    clientId: val ? Number(val) : undefined,
                                }));
                            }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">时间范围</label>
                        <RangePicker
                            className="w-full"
                            value={
                                tempFilters.dailyDateStart && tempFilters.dailyDateEnd
                                    ? [
                                        dayjs(tempFilters.dailyDateStart.toString(), 'YYYYMMDD'),
                                        dayjs(tempFilters.dailyDateEnd.toString(), 'YYYYMMDD'),
                                    ] as [Dayjs, Dayjs]
                                    : null
                            }
                            onChange={(dates) => {
                                if (dates?.[0] && dates?.[1]) {
                                    setTempFilters((prev) => ({
                                        ...prev,
                                        dailyDateStart: parseInt(dates[0]!.format('YYYYMMDD')),
                                        dailyDateEnd: parseInt(dates[1]!.format('YYYYMMDD')),
                                    }));
                                } else {
                                    setTempFilters((prev) => ({
                                        ...prev,
                                        dailyDateStart: undefined,
                                        dailyDateEnd: undefined,
                                    }));
                                }
                            }}
                            format="YYYY-MM-DD"
                        />
                    </div>
                    <div className="flex items-end gap-2">
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            className="flex-1"
                        >
                            查询
                        </Button>
                        <Button onClick={handleReset} className="flex-1">
                            重置
                        </Button>
                    </div>
                    <div className="flex items-end">
                        <Button
                            type="default"
                            icon={<ExportOutlined />}
                            onClick={handleExport}
                            disabled={!pageResult?.total}
                        >
                            导出
                        </Button>
                    </div>
                </div>
            </Card>

            <Card title="列表">
                <Table
                    columns={columns}
                    dataSource={pageResult?.list || []}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 800 }}
                    className="mb-4"
                />

                <TablePaginationBar
                    total={pageResult?.total || 0}
                    current={page}
                    pageSize={pageSize}
                    onChange={(newPage, newPageSize) => {
                        setPage(newPage);
                        setPageSize(newPageSize);
                    }}
                />
            </Card>

            <ParcelsDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default RealtimeEstimatedIncomePage;
